import { UPDATE_TYPE_LABELS, UPDATE_TYPE_ICONS, type Update } from '@/types/update'
import { ImageGallery } from '@/components/images'

interface TimelineViewProps {
  updates: Update[]
  showChildren?: boolean
  onEdit?: (update: Update) => void
  onDelete?: (id: string) => void
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

// Group updates by date
function groupUpdatesByDate(updates: Update[]): Map<string, Update[]> {
  const groups = new Map<string, Update[]>()

  updates.forEach((update) => {
    const date = new Date(update.createdAt)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    let dateKey: string

    if (date.toDateString() === today.toDateString()) {
      dateKey = 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      dateKey = 'Yesterday'
    } else {
      dateKey = date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    }

    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(update)
  })

  return groups
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const TYPE_COLORS: Record<string, string> = {
  note: 'bg-blue-500',
  food: 'bg-orange-500',
  toilet: 'bg-purple-500',
  activity: 'bg-green-500',
  medicine: 'bg-red-500',
}

export function TimelineView({
  updates,
  showChildren = true,
  onEdit,
  onDelete,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: TimelineViewProps) {
  const groupedUpdates = groupUpdatesByDate(updates)

  return (
    <div className="space-y-6">
      {Array.from(groupedUpdates.entries()).map(([dateLabel, dateUpdates]) => (
        <div key={dateLabel}>
          {/* Date Header */}
          <div className="mb-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-sm font-medium text-gray-500">{dateLabel}</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          {/* Timeline Items */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

            <div className="space-y-4">
              {dateUpdates.map((update) => (
                <div key={update.id} className="relative flex gap-4">
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm ${TYPE_COLORS[update.type]} text-white shadow-sm`}
                    >
                      {UPDATE_TYPE_ICONS[update.type]}
                    </div>
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 pb-2">
                    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                      {/* Header */}
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          {UPDATE_TYPE_LABELS[update.type]}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">
                            {formatTime(update.createdAt)}
                          </span>
                          {onEdit && (
                            <button
                              onClick={() => onEdit(update)}
                              className="text-xs text-primary-500 hover:text-primary-700"
                            >
                              Edit
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={() => onDelete(update.id)}
                              className="text-xs text-red-400 hover:text-red-600"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      <p className="whitespace-pre-wrap text-sm text-gray-600 ph-no-capture">
                        {update.content}
                      </p>

                      {/* Images */}
                      {update.images && update.images.length > 0 && (
                        <ImageGallery
                          images={update.images}
                          className="mt-2"
                        />
                      )}

                      {/* Children Tags */}
                      {showChildren && update.children.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {update.children.map((child) => (
                            <span
                              key={child.id}
                              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 ph-no-capture"
                            >
                              {child.firstName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-2">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            {isLoadingMore ? 'Loading...' : 'Load older updates'}
          </button>
        </div>
      )}
    </div>
  )
}
